import { Component, OnInit, OnDestroy, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
@Component({
  selector: 'app-voice-input',
  templateUrl: './voice-input.component.html',
  styleUrls: ['./voice-input.component.scss']
})
export class VoiceInputComponent implements OnInit, OnDestroy {
  /** Flag to track if the mic is currently recording */
  isListening = false;

  /** Flag to track if speech is actively being detected */
  isSpeechActive = false;

  /** Flag to track transition from speech timeout to breathing */
  isSpeechEnding = false;

  /** Controls visibility of the tooltip UI */
  showTooltip = false;

  /** Stores partial (interim) speech recognition result */
  interimText = '';

  /** Stores confirmed (final) speech recognition result */
  finalText = '';

  /** Web Speech API's SpeechRecognition instance */
  recognition: any;

  /** Timeout reference to detect silence */
  public silenceTimeout: any;

  /** Timeout reference to reset speech activity state */
  public speechActivityTimeout: any;

  /** Timestamp of the last detected speech input */
  public lastSpeechTime: number = 0;

  /**
   * Output event emitter to send the recognized speech text
   * to the parent component.
   */
  @Output() speechText = new EventEmitter<string>();

  /**
   * Output event emitter to send the microphone status
   * to the parent component.
   */
  @Output() micStatus = new EventEmitter<boolean>();

  /**
   * Output event emitter to emit the speech error.
   */
  @Output() speechError = new EventEmitter<string>();

  /** Error template for VOICE INPUT */
  private readonly voiceInputError = (error: any) => 
    `[VOICE INPUT] - ${JSON.stringify(error)}`;

  /**
   * Language mapping for speech recognition API.
   * Matches app's language code to Web Speech locale.
   */
  languageOptions: Record<string, string> = {
    en: 'en-US',
    jp: 'ja-JP',
    zhCN: 'zh-CN'
  };

  constructor(
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly loggerService: LoggerService
  ) {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
    }
  }

  /**
   * Initializes the speech recognition logic and binds all event listeners.
   */
  ngOnInit(): void {
    try {
      if (!this.recognition) return;

      this.recognition.onresult = (event: any) => {
        let interim = '';
        let final = '';

        // Process all speech segments in the event
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }

        // Update the component state
        this.interimText = interim;
        this.finalText += final;
        this.lastSpeechTime = Date.now();
        this.activateSpeechFeedback();
        this.resetSilenceTimeout();

        // Emit full transcript (final + interim)
        this.speechText.emit(this.finalText + this.interimText);
        this.cdRef.detectChanges();
      };

      // Handle speech recognition errors
      this.recognition.onerror = (event: any) => {
        this.loggerService.logError(this.voiceInputError(event), "onerror");
      
        if (event.error === 'no-speech') {
          this.stopListening();
        }
        if (event.error === 'not-allowed') {
          this.stopListening();
          this.speechError.emit('MIC_PERMISSION_DENIED');
        }
        if (event.error === 'network') {
          this.stopListening();
          this.speechError.emit('MIC_PERMISSION_NETWORK_ERROR');
        }
        else if(event.error !== 'no-speech' && event.error !== 'not-allowed' && event.error !== 'network'){
          this.stopListening();
          this.speechError.emit('MIC_UNKNOWN_ERROR');
        }
      };

      // Update last speech time on speech start
      this.recognition.onspeechstart = () => {
        this.lastSpeechTime = Date.now();
        this.activateSpeechFeedback();
        this.resetSilenceTimeout();
      };

      // Automatically restart recognition on unexpected end
      this.recognition.onend = () => {
        if (this.isListening) {
          this.recognition.start();
        }
      };
    } catch (error) {
      this.loggerService.logError(this.voiceInputError(error), "ngOnInit");
    }
  }

  /**
   * Cleans up recognition listeners and timeout on destroy.
   */
  ngOnDestroy(): void {
    if (this.recognition) this.recognition.stop();
    clearTimeout(this.silenceTimeout);
    clearTimeout(this.speechActivityTimeout);
  }

  /**
   * Toggles the microphone listening state.
   * Shows tooltip briefly when mic starts.
   */
  toggleSpeechRecognition(): void {
    if (!this.recognition) {
      this.loggerService.logError(this.voiceInputError("Speech Recognition API is not supported in this browser."), "toggleSpeechRecognition");
      alert('Speech Recognition API is not supported in this browser.');
      return;
    }

    // Show tooltip for 2s seconds when starting
    this.showTooltip = !this.isListening;
    setTimeout(() => {
      this.showTooltip = false;
    }, 2000);

    this.isListening ? this.stopListening() : this.startListening();
  }

  /**
   * Starts speech recognition with current app language.
   */
  startListening(): void {
    this.micStatus.emit(true);
    this.isListening = true;
    this.interimText = '';
    this.finalText = '';

    const currentLang = this.translate.currentLang;
    this.recognition.lang = this.languageOptions[currentLang] || 'en-US';

    this.recognition.start();
    this.resetSilenceTimeout();
  }

  /**
   * Stops speech recognition and resets related states.
   */
  stopListening(): void {
    this.micStatus.emit(false);
    this.isListening = false;
    this.isSpeechActive = false;
    this.isSpeechEnding = false;
    this.interimText = '';
    clearTimeout(this.silenceTimeout);
    clearTimeout(this.speechActivityTimeout);
    this.recognition.stop();
    this.cdRef.detectChanges();
  }

  /**
   * Resets the silence timeout.
   * Automatically stops recognition after 5s of inactivity.
   */
  public resetSilenceTimeout(): void {
    clearTimeout(this.silenceTimeout);
    this.silenceTimeout = setTimeout(() => {
      if (this.isListening && Date.now() - this.lastSpeechTime >= 4500) {
        this.stopListening();
      }
    }, 5000);
  }

  /**
   * Activates speech feedback visual animation when speech is detected.
   * Shows pulsating effect during speech, then transitions to breathing from speech scale.
   */
  public activateSpeechFeedback(): void {
    this.isSpeechActive = true;
    this.isSpeechEnding = false;
    clearTimeout(this.speechActivityTimeout);
    
    // After 350ms of no new speech, transition to breathing from current scale
    this.speechActivityTimeout = setTimeout(() => {
      this.isSpeechActive = false;
      this.isSpeechEnding = true;
      this.cdRef.detectChanges();
      
      // After one breathing cycle, return to normal breathing
      setTimeout(() => {
        this.isSpeechEnding = false;
        this.cdRef.detectChanges();
      }, 4000);
    }, 350);
    
    this.cdRef.detectChanges();
  }
}